import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Container, 
  Grid, 
  Card, 
  CardContent,
  Chip,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
  Tooltip,
  IconButton,
  InputLabel,
  Select,
  useMediaQuery,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { ChevronRight, InfoOutlined, Close } from '@mui/icons-material';
import './styles.scss';

// Types based on backend schema
export interface WorkStyleQuizResult {
  partnershipOptions: string[];
  compensationModel: string[];
  presentationType: string[];
  minimumBudget?: string;
}

export interface WorkStyleQuizRef {
  handleBegin: () => void;
}

export interface WorkStyleQuizProps {
  onComplete?: () => void;
  onResultChange?: (result: WorkStyleQuizResult | null) => void;
  onBeginShown?: () => void;
  onQuizStarted?: () => void;
  onStepChange?: (step: number, totalSteps: number) => void;
  currentStep?: number;
  initialData?: WorkStyleQuizResult | null;
}

// Data from backend enums
const partnershipOptions = [
  { value: 'fullService', label: 'Full Service', description: 'I create the design, create drawings, source trade specifications, handle the execution and logistics with client approval.' },
  { value: 'consultant', label: 'Consultant', description: 'In person ideas, styling, paint selection or retail shopping.' },
  { value: 'eDesign', label: 'E-Design', description: 'I create the design electronically, source the items and client handles the execution and logistics.' }
];

const compensationModels = [
  { 
    value: 'hourly', 
    label: 'Hourly Rate', 
    description: 'One of the most straightforward ways interior designers charge is by the hour. Designers often set hourly rates based on their experience and expertise. This method works well for projects that require ongoing consultation, such as styling or updates. However, it\'s essential to understand the total hours required upfront, as this can vary depending on the project\'s complexity.\n\nBest for: Clients who prefer flexibility or have smaller, ongoing design needs.\nConsiderations: Be sure to ask for an estimate of total hours to avoid surprises.'
  },
  { 
    value: 'flatFee', 
    label: 'Flat Fee', 
    description: 'A flat fee or fixed-rate pricing model is when the designer charges a set amount for the entire project, regardless of the time spent. This model is popular for larger projects, such as full-home renovations or commercial spaces, where the designer can anticipate the work involved.\n\nBest for: Homeowners who prefer knowing the total cost upfront.\nConsiderations: Ensure the scope of work is clearly defined in the contract to avoid additional charges.'
  },
  { 
    value: 'percentageOfCost', 
    label: 'Percentage of Project Cost', 
    description: 'Many interior designers charge a percentage of the total project cost, typically ranging from 10% to 30%. This means the designer\'s fee will fluctuate based on the overall budget for materials, furnishings, and construction. This pricing model works well for larger projects where the designer is responsible for significant purchasing decisions.\n\nBest for: High-budget projects where the designer oversees large purchases and installation.\nConsiderations: As costs increase, so will the designer\'s fee. Be sure to set a realistic budget.'
  },
  { 
    value: 'squareFootage', 
    label: 'Per Square Foot Rate', 
    description: 'Designers may charge by the square foot for larger commercial or residential projects. This model allows the designer to scale their fees based on the size of the space, making it a popular choice for offices, restaurants, and luxury home designs.\n\nBest for: Large-scale commercial projects or expansive residential designs.\nConsiderations: Make sure to clarify what services are included in the per-square-foot price to avoid miscommunication.'
  },
  { 
    value: 'retainer', 
    label: 'Design Retainer', 
    description: 'Some interior designers may ask for a design retainer upfront, especially for high-end projects. This retainer often covers the initial consultation, design planning, and other upfront services. Once the retainer is used, the designer may switch to an hourly rate or percentage-based fee.\n\nBest for: Larger or more complex projects that require in-depth planning.\nConsiderations: Ask how the retainer is applied to the overall project and if it\'s refundable.'
  },
  { 
    value: 'hybrid', 
    label: 'Combination Models', 
    description: 'Many designers use a combination of the above models, such as charging an hourly rate for consultations and a flat fee for the design plan. Some may also offer packages that bundle different services, such as sourcing, project management, and installation.\n\nBest for: Projects that require a balance of consultation and full-service design.\nConsiderations: Clearly outline which services are billed under each pricing structure.'
  }
];

const presentationTypes = [
  { value: 'boards', label: 'Design Boards', description: 'Traditional mood boards and material boards' },
  { value: 'twoD', label: '2D Renderings', description: 'Flat drawings and floor plans' },
  { value: 'threeD', label: '3D Renderings', description: 'Three-dimensional visualizations' },
  { value: 'virtual', label: 'Virtual Reality', description: 'Immersive VR experiences' }
];

const budgetRanges = [
  { value: 'UNDER_3000', label: 'Under $3,000' },
  { value: 'FROM_3001_TO_5_HUNDRED', label: '$3,001 - $5,000' },
  { value: 'FROM_5001_TO_10_THOUSAND', label: '$5,001 - $10,000' },
  { value: 'FROM_10001_TO_15_THOUSAND', label: '$10,001 - $15,000' },
  { value: 'FROM_15001_TO_20_THOUSAND', label: '$15,001 - $20,000' },
  { value: 'FROM_20001_TO_25_THOUSAND', label: '$20,001 - $25,000' },
  { value: 'FROM_25001_TO_30_THOUSAND', label: '$25,001 - $30,000' },
  { value: 'FROM_30001_TO_35_THOUSAND', label: '$30,001 - $35,000' },
  { value: 'FROM_35001_TO_40_THOUSAND', label: '$35,001 - $40,000' },
  { value: 'FROM_40001_TO_45_THOUSAND', label: '$40,001 - $45,000' },
  { value: 'FROM_45001_TO_50_THOUSAND', label: '$45,001 - $50,000+' }
];

export const WorkStyleQuiz = forwardRef<WorkStyleQuizRef, WorkStyleQuizProps>(({
  onComplete,
  onResultChange,
  onBeginShown,
  onQuizStarted,
  onStepChange,
  currentStep: externalCurrentStep,
  initialData
}, ref) => {
  const [showBegin, setShowBegin] = useState(true);
  const currentStep = externalCurrentStep || 1;
  const totalSteps = 4;

  // Step data - initialize from initialData if provided
  const [selectedPartnershipOptions, setSelectedPartnershipOptions] = useState<string[]>(
    initialData?.partnershipOptions || []
  );
  const [selectedCompensationModels, setSelectedCompensationModels] = useState<string[]>(
    initialData?.compensationModel || []
  );
  const [selectedPresentationTypes, setSelectedPresentationTypes] = useState<string[]>(
    initialData?.presentationType || []
  );
  const [minimumBudget, setMinimumBudget] = useState<string>(
    initialData?.minimumBudget || ''
  );

  const [isCompleted, setIsCompleted] = useState(false);
  
  // Mobile tooltip state
  const [mobileTooltipOpen, setMobileTooltipOpen] = useState<string | null>(null);
  const isMobile = useMediaQuery('(max-width:600px)');

  const handleMobileTooltipOpen = (modelValue: string) => {
    setMobileTooltipOpen(modelValue);
  };

  const handleMobileTooltipClose = () => {
    setMobileTooltipOpen(null);
  };

  // Notify parent about step changes
  useEffect(() => {
    if (!showBegin && onStepChange) {
      onStepChange(currentStep, totalSteps);
    }
  }, [currentStep, showBegin, onStepChange, totalSteps]);

  // Check completion status - only mark as completed when user has actually finished all steps
  useEffect(() => {
    const completed = !showBegin && currentStep >= totalSteps;
    if (completed !== isCompleted) {
      setIsCompleted(completed);
      if (completed && onComplete) {
        onComplete();
      }
    }
  }, [currentStep, showBegin, isCompleted, onComplete, totalSteps]);

  // Update result when data changes
  useEffect(() => {
    if (!showBegin && onResultChange) {
      const result: WorkStyleQuizResult = {
        partnershipOptions: selectedPartnershipOptions,
        compensationModel: selectedCompensationModels,
        presentationType: selectedPresentationTypes,
        minimumBudget: minimumBudget || undefined
      };
      onResultChange(result);
    }
  }, [selectedPartnershipOptions, selectedCompensationModels, selectedPresentationTypes, minimumBudget, showBegin, onResultChange]);

  const handleBegin = () => {
    setShowBegin(false);
    if (onQuizStarted) {
      onQuizStarted();
    }
  };

  // Expose handleBegin to parent via ref
  useImperativeHandle(ref, () => ({
    handleBegin
  }), []);

  const togglePartnershipOption = (option: string) => {
    setSelectedPartnershipOptions(prev => 
      prev.includes(option) 
        ? prev.filter(o => o !== option)
        : [...prev, option]
    );
  };

  const toggleCompensationModel = (model: string) => {
    setSelectedCompensationModels(prev => 
      prev.includes(model) 
        ? prev.filter(m => m !== model)
        : [...prev, model]
    );
  };

  const togglePresentationType = (type: string) => {
    setSelectedPresentationTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  // Show Begin screen
  if (showBegin) {
    if (onBeginShown) onBeginShown();
    
    return (
      <Container maxWidth="md" sx={{ pl: 2, pr: 2, pt: 6 }}>
        <Box textAlign="center" pt={0} pb={6}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
            Work Style Assessment
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Help us understand how you prefer to work with clients.
            <br />
            This includes your service offerings, pricing model, and presentation style.
          </Typography>
          <Button 
            variant="outlined" 
            size="large" 
            color="secondary"
            endIcon={<ChevronRight sx={{ width: 16, height: 16 }} />}
            onClick={handleBegin}
          >
            Begin
          </Button>
        </Box>
      </Container>
    );
  }

  const renderStep1 = () => (
    <Container maxWidth="md" sx={{ pl: 2, pr: 2, pt: 6 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
          Please select which of these partnership options you provide:
        </Typography>
        <Typography variant="body1" color="text.secondary">
          (Select All That Apply)
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {partnershipOptions.map((option) => (
          <Grid item xs={12} sm={6} md={4} key={option.value}>
            <Card
              onClick={() => togglePartnershipOption(option.value)}
              variant={selectedPartnershipOptions.includes(option.value) ? 'elevation' : 'outlined'}
              sx={{
                cursor: 'pointer',
                height: '100%',
                minHeight: '180px',
                boxShadow: 'none',
                border: selectedPartnershipOptions.includes(option.value) 
                  ? '2px solid' 
                  : '1px solid',
                borderColor: selectedPartnershipOptions.includes(option.value) 
                  ? 'secondary.main' 
                  : 'divider',
                backgroundColor: selectedPartnershipOptions.includes(option.value)
                  ? 'secondary.main'
                  : 'transparent',
                '&:hover': {
                  borderColor: 'secondary.main',
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 1.5,
                    fontWeight: 'bold',
                    color: selectedPartnershipOptions.includes(option.value) ? '#FFFFFF' : 'secondary.main',
                    textAlign: 'left'
                  }}
                >
                  {option.label}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: selectedPartnershipOptions.includes(option.value) ? '#FFFFFF' : 'text.secondary',
                    textAlign: 'left',
                    fontWeight: 'normal'
                  }}
                >
                  {option.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );

  const renderStep2 = () => {

    return (
      <Container maxWidth="lg" sx={{ pl: 2, pr: 2, pt: 6 }}>
        <Box textAlign="center" mb={4}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
            How do you charge?
          </Typography>
          <Typography variant="body1" color="text.secondary">
            (Select All That Apply)
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {compensationModels.map((model) => (
            <Grid item xs={12} sm={6} md={4} key={model.value}>
              <Card
                onClick={() => toggleCompensationModel(model.value)}
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  minHeight: isMobile ? '44px' : '180px',
                  border: selectedCompensationModels.includes(model.value) 
                    ? '2px solid' 
                    : '1px solid',
                  borderColor: selectedCompensationModels.includes(model.value) 
                    ? 'secondary.main' 
                    : 'divider',
                  backgroundColor: selectedCompensationModels.includes(model.value)
                    ? 'secondary.main'
                    : 'transparent',
                  boxShadow: 'none',
                  '&:hover': {
                    borderColor: 'secondary.main',
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <CardContent sx={{ 
                  p: isMobile ? 2 : 3, 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  justifyContent: 'flex-start' 
                }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={isMobile ? 1 : 1.5}>
                    <Typography 
                      variant={isMobile ? "body1" : "h6"}
                      sx={{ 
                        fontWeight: 'bold',
                        color: selectedCompensationModels.includes(model.value) ? '#FFFFFF' : 'secondary.main',
                        textAlign: 'left',
                        flex: 1,
                        fontSize: isMobile ? '0.9rem' : undefined
                      }}
                    >
                      {model.label}
                    </Typography>
                    {isMobile ? (
                      <>
                        <IconButton
                          size="small"
                          sx={{
                            width: 44,
                            height: 44,
                            color: selectedCompensationModels.includes(model.value) ? '#FFFFFF' : 'secondary.main',
                            '&:hover': {
                              backgroundColor: selectedCompensationModels.includes(model.value) 
                                ? 'rgba(255, 255, 255, 0.1)' 
                                : 'rgba(233, 30, 99, 0.1)'
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMobileTooltipOpen(model.value);
                          }}
                        >
                          <InfoOutlined fontSize="small" />
                        </IconButton>
                        
                        {/* Mobile Dialog */}
                        <Dialog
                          open={mobileTooltipOpen === model.value}
                          onClose={handleMobileTooltipClose}
                          maxWidth="sm"
                          fullWidth
                        >
                          <DialogTitle>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography variant="h6" fontWeight="bold">
                                {model.label}
                              </Typography>
                              <IconButton
                                onClick={handleMobileTooltipClose}
                                size="small"
                                sx={{ color: 'text.secondary' }}
                              >
                                <Close />
                              </IconButton>
                            </Box>
                          </DialogTitle>
                          <DialogContent>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                whiteSpace: 'pre-line',
                                lineHeight: 1.6
                              }}
                            >
                              {model.description}
                            </Typography>
                          </DialogContent>
                        </Dialog>
                      </>
                    ) : (
                      <Tooltip 
                        title={
                          <Box sx={{ whiteSpace: 'pre-line', maxWidth: 400 }}>
                            {model.description}
                          </Box>
                        }
                        placement="top"
                        arrow
                      >
                        <IconButton
                          size="small"
                          sx={{
                            width: 44,
                            height: 44,
                            color: selectedCompensationModels.includes(model.value) ? '#FFFFFF' : 'secondary.main',
                            '&:hover': {
                              backgroundColor: selectedCompensationModels.includes(model.value) 
                                ? 'rgba(255, 255, 255, 0.1)' 
                                : 'rgba(233, 30, 99, 0.1)'
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <InfoOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  };

  const renderStep3 = () => (
    <Container maxWidth="md" sx={{ pl: 2, pr: 2, pt: 6 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
          How do you prefer to present your designs?
        </Typography>
        <Typography variant="body1" color="text.secondary">
          (Select All That Apply)
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {presentationTypes.map((type) => (
          <Grid item xs={12} sm={6} md={4} key={type.value}>
            <Card
              onClick={() => togglePresentationType(type.value)}
              variant={selectedPresentationTypes.includes(type.value) ? 'elevation' : 'outlined'}
              sx={{
                cursor: 'pointer',
                height: '100%',
                minHeight: '180px',
                boxShadow: 'none',
                border: selectedPresentationTypes.includes(type.value) 
                  ? '2px solid' 
                  : '1px solid',
                borderColor: selectedPresentationTypes.includes(type.value) 
                  ? 'secondary.main' 
                  : 'divider',
                backgroundColor: selectedPresentationTypes.includes(type.value)
                  ? 'secondary.main'
                  : 'transparent',
                '&:hover': {
                  borderColor: 'secondary.main',
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 1.5,
                    fontWeight: 'bold',
                    color: selectedPresentationTypes.includes(type.value) ? '#FFFFFF' : 'secondary.main',
                    textAlign: 'left'
                  }}
                >
                  {type.label}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: selectedPresentationTypes.includes(type.value) ? '#FFFFFF' : 'text.secondary',
                    textAlign: 'left',
                    fontWeight: 'normal'
                  }}
                >
                  {type.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );

  const renderStep4 = () => (
    <Container maxWidth="md" sx={{ pl: 2, pr: 2, pt: 6 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
          What's your minimum project budget?
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select the minimum budget range you typically work with
        </Typography>
      </Box>

      <Box maxWidth="400px" mx="auto">
        <FormControl fullWidth>
          <InputLabel id="budget-select-label">Select Budget Range</InputLabel>
          <Select
            labelId="budget-select-label"
            value={minimumBudget || ''}
            onChange={(e) => setMinimumBudget(e.target.value)}
            label="Select Budget Range"
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300, // Fixed height for 10 items (30px per item)
                  overflow: 'auto',
                },
              },
            }}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#E91E63'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#E91E63'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#E91E63'
              }
            }}
          >
            <MenuItem value="" disabled>
              Select One
            </MenuItem>
            {budgetRanges.map((budget) => (
              <MenuItem key={budget.value} value={budget.value}>
                {budget.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Container>
  );

  return (
    <Box sx={{ pb: 10 }}>
      <Box py={4}>
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
      </Box>
    </Box>
  );
});

WorkStyleQuiz.displayName = 'WorkStyleQuiz';

export default WorkStyleQuiz;
